import type { JsonValue } from "@e308/core";
import { formatMicro, microFromString } from "./exact-numbers.js";
import { cloneLogisticsState } from "./logistics-clone.js";
import {
  addTroops,
  EMPTY_TROOPS,
  requiredBorder,
  subtractTroops,
  travelDeadline,
} from "./logistics-math.js";
import { startTravel } from "./logistics-state.js";
import type {
  AutoReinforceOrder,
  LogisticsState,
  TransitOrder,
  TroopComposition,
} from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";

export interface LogisticsAdvance {
  readonly state: LogisticsState & JsonValue;
  readonly returned: TroopComposition;
}

export interface AutoReinforceAdvance {
  readonly state: LogisticsState & JsonValue;
  readonly home: TroopComposition;
}

export function advanceAutoReinforce(
  state: LogisticsState,
  home: TroopComposition,
  nowMs: number,
  travelMultiplier: string,
  stepMs: number,
  validBorders: ReadonlySet<string>,
): AutoReinforceAdvance {
  if (nowMs < state.nextAutoReinforceAtMs)
    return { state: state as LogisticsState & JsonValue, home };
  let current: LogisticsState = cloneLogisticsState({
    ...state,
    nextAutoReinforceAtMs: (Math.floor(nowMs / 5_000) + 1) * 5_000,
  });
  let reserve = home;
  for (const order of current.autoReinforce) {
    if (!validBorders.has(order.destination)) continue;
    const sent = autoShipment(current, reserve, order);
    if (UNIT_IDS.every((id) => microFromString(sent[id]) === 0n)) continue;
    reserve = subtractTroops(reserve, sent);
    current = startTravel(
      current,
      "home",
      order.destination,
      sent,
      nowMs,
      travelMultiplier,
      stepMs,
    ) as LogisticsState;
  }
  return { state: current as LogisticsState & JsonValue, home: reserve };
}

export function advanceLogistics(
  state: LogisticsState,
  nowMs: number,
  travelMultiplier: string,
  stepMs: number,
  validBorders: ReadonlySet<string>,
): LogisticsAdvance {
  let current: LogisticsState = redirectMissingDestinations(
    state,
    nowMs,
    travelMultiplier,
    stepMs,
    validBorders,
  ) as LogisticsState;
  let returned = EMPTY_TROOPS;
  const completed = current.transits.filter((transit) => nowMs >= transit.completesAtMs);
  current = cloneLogisticsState({
    ...current,
    transits: current.transits.filter((transit) => nowMs < transit.completesAtMs),
  });
  for (const transit of completed) {
    const result = completeTransit(current, transit, nowMs, travelMultiplier, stepMs, validBorders);
    current = result.state;
    returned = addTroops(returned, result.returned);
  }
  return { state: current as LogisticsState & JsonValue, returned };
}

function completeTransit(
  state: LogisticsState,
  transit: TransitOrder,
  nowMs: number,
  multiplier: string,
  stepMs: number,
  validBorders: ReadonlySet<string>,
): LogisticsAdvance {
  if (transit.destination === "home") {
    if (transit.finalDestination && validBorders.has(transit.finalDestination))
      return {
        state: appendTransit(state, transit, transit.finalDestination, nowMs, multiplier, stepMs),
        returned: EMPTY_TROOPS,
      };
    return { state: state as LogisticsState & JsonValue, returned: transit.units };
  }
  if (!validBorders.has(transit.destination))
    return {
      state: appendTransit(state, transit, "home", nowMs, multiplier, stepMs),
      returned: EMPTY_TROOPS,
    };
  const position = requiredBorder(state, transit.destination);
  const arrived = cloneLogisticsState({
    ...state,
    borders: {
      ...state.borders,
      [transit.destination]: { ...position, troops: addTroops(position.troops, transit.units) },
    },
  });
  return transit.returnAfterArrival
    ? {
        state: startTravel(
          arrived,
          transit.destination,
          "home",
          transit.units,
          nowMs,
          multiplier,
          stepMs,
        ),
        returned: EMPTY_TROOPS,
      }
    : { state: arrived as LogisticsState & JsonValue, returned: EMPTY_TROOPS };
}

function appendTransit(
  state: LogisticsState,
  prior: TransitOrder,
  destination: string,
  nowMs: number,
  multiplier: string,
  stepMs: number,
): LogisticsState & JsonValue {
  return cloneLogisticsState({
    ...state,
    nextTransitId: state.nextTransitId + 1,
    transits: [
      ...state.transits,
      {
        id: `transit-${state.nextTransitId}`,
        units: prior.units,
        destination,
        finalDestination: null,
        completesAtMs: travelDeadline(nowMs, multiplier, stepMs),
        returnAfterArrival: false,
      },
    ],
  }) as LogisticsState & JsonValue;
}

function redirectMissingDestinations(
  state: LogisticsState,
  nowMs: number,
  multiplier: string,
  stepMs: number,
  valid: ReadonlySet<string>,
): LogisticsState & JsonValue {
  const changed = state.transits.some(
    (transit) => transit.destination !== "home" && !valid.has(transit.destination),
  );
  if (!changed) return state as LogisticsState & JsonValue;
  return cloneLogisticsState({
    ...state,
    transits: state.transits.map((transit) =>
      transit.destination !== "home" && !valid.has(transit.destination)
        ? {
            ...transit,
            destination: "home",
            finalDestination: null,
            returnAfterArrival: false,
            completesAtMs: travelDeadline(nowMs, multiplier, stepMs),
          }
        : transit,
    ),
  }) as LogisticsState & JsonValue;
}

function autoShipment(
  state: LogisticsState,
  home: TroopComposition,
  order: AutoReinforceOrder,
): TroopComposition {
  const position = requiredBorder(state, order.destination);
  return Object.freeze(
    Object.fromEntries(
      UNIT_IDS.map((id) => {
        const enRoute = state.transits
          .filter(
            (transit) =>
              !transit.returnAfterArrival &&
              (transit.destination === order.destination ||
                transit.finalDestination === order.destination),
          )
          .reduce((sum, transit) => sum + microFromString(transit.units[id]), 0n);
        const available = microFromString(home[id]);
        const desired = order.desired[id];
        const needed =
          desired === null
            ? available
            : microFromString(desired) - microFromString(position.troops[id]) - enRoute;
        return [
          id,
          formatMicro((needed <= 0n ? 0n : needed < available ? needed : available) as never),
        ];
      }),
    ),
  ) as TroopComposition;
}
